import { useSubscription } from '@apollo/client';
import {
  Button,
  Card,
  Empty,
  Icons,
  List,
  notification,
  Radio,
  RadioChangeEvent,
  Select,
  Skeleton,
  Space,
  Spin,
  Tag,
  Typography,
} from '@pankod/refine-antd';
import { IResourceComponentsProps, useCan, useNavigation } from '@pankod/refine-core';
import { OrderShowDrawer } from 'components/restaurant-sales';
import { SearchWithDateRange } from 'components/search/SearchWithDateRange';
import { EStatus } from 'enums';
import { useUserData } from 'hooks';
import { IQueryOptionals, ISale } from 'interfaces';
import {
  calculateWaitingTime,
  formatTime,
  getTagColor,
  getTrackingStatus,
  getTrackingStatusColor,
  STATUS_MAPPING_REVERSE,
} from 'pages/restaurant-sales/list';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RiEyeLine, RiPrinterLine } from 'react-icons/ri';
import { SearchIndex } from 'services/meilisearch';
import {
  EInvoiceType,
  getSaleInvoice,
  getSales,
  getSalesAndProductStatusCount,
  SalesAndProductStatusCountResponse,
} from 'services/sale';
import { MESSAGE_ADDED_SUBSCRIPTION } from 'services/subscription';
import { getUsers } from 'services/user';
import {
  capitalize,
  formatToLocalDateTime,
  getDateRangeOfLastXDays,
  previewPdfFromBase64,
} from 'utilities/shared';
import MetaData from 'utilities/metadata';
import styles from './style.module.css';

const { Text } = Typography;
type StatusType = 'all' | 'new' | 'processing' | 'completed';
type IQuery = IQueryOptionals & { statuses?: EStatus[]; user?: string };
type SaleWithTrackingStatusType = ISale & { trackingStatus: string };

const BOTTOM_SCROLL_THRESHOLD = 150; // in pixels

export const StaffList: React.FC<IResourceComponentsProps> = () => {
  const [sales, setSales] = useState<SaleWithTrackingStatusType[]>([]);
  const [totalSales, setTotalSales] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [dateRange, setDateRange] = useState<{ startDate?: string; endDate?: string }>(() => {
    const [startDate, endDate] = getDateRangeOfLastXDays(0);
    return {
      startDate: formatToLocalDateTime(startDate, 0, 0), // 12:00 AM
      endDate: formatToLocalDateTime(endDate, 23, 59), // 11:59 PM
    };
  });
  const [status, setStatus] = useState<StatusType>('all');
  const [users, setUsers] = useState<{ id: string; name: string }[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>();

  // Order Show Drawer
  const [openShowDrawer, setOpenShowDrawer] = useState<boolean>(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const isInitialMount = useRef(true);
  const isFetching = useRef(false);
  const { push } = useNavigation();
  const { data: newSubscriptionData } = useSubscription(MESSAGE_ADDED_SUBSCRIPTION);
  const [statusCount, setStatusCount] = useState<SalesAndProductStatusCountResponse | null>(null);
  const userData = useUserData();

  const { data: canCreateRestaurantPos } = useCan({
    resource: 'restaurant-pos',
    action: 'list',
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setSales((prevSales) =>
        prevSales.map((sale) => {
          const trackingStatus = getTrackingStatus(sale);
          return { ...sale, trackingStatus };
        })
      );
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const query = useMemo<IQuery>(() => {
    return {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      searchString: searchText,
      user: selectedUser === 'all' ? undefined : selectedUser,
      statuses: status === 'all' ? undefined : [STATUS_MAPPING_REVERSE[capitalize(status)]],
      start: page,
      limit: 10,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.startDate, dateRange.endDate, searchText, selectedUser, status, page]);

  const fetchSales = useCallback(async (query: IQuery) => {
    if (isFetching.current) return;

    try {
      setIsLoading(true);
      isFetching.current = true;

      const response = await getSales(query);
      const salesWithTrackingStatus = response.data.map((sale) => {
        const trackingStatus = getTrackingStatus(sale);
        return { ...sale, trackingStatus };
      });

      setSales((prevSales) => [...prevSales, ...salesWithTrackingStatus]);
      setTotalSales(response.total);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    } finally {
      setIsLoading(false);
      isFetching.current = false;
    }
  }, []);

  const fetchStatusCount = useCallback(async () => {
    try {
      const response = await getSalesAndProductStatusCount({
        startDate: query.startDate,
        endDate: query.endDate,
        userId: query.user,
      });

      setStatusCount(response);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err?.message || 'Failed to fetch status count',
      });
    }
  }, [query.endDate, query.startDate, query.user]);

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        setIsLoading(true);
        const response = await getUsers();
        const users = response?.data ?? [];
        const userOptions = [{ id: 'all', name: 'All' }].concat(
          users.map((u) => ({ id: u.id, name: u.name }))
        );
        setUsers(userOptions);
        setSelectedUser(userOptions[0]?.id ?? undefined);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err?.message || 'Failed to fetch warehouses',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchWarehouses();
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    fetchSales(query);
  }, [fetchSales, query]);

  useEffect(() => {
    fetchStatusCount();
  }, [fetchStatusCount]);

  // Update the kitchen list when new data is received
  useEffect(() => {
    if (newSubscriptionData && newSubscriptionData.messageAdded) {
      let parsedData: { instanceId: string; message: string } = {
        instanceId: '',
        message: '',
      };
      try {
        parsedData = JSON.parse(newSubscriptionData.messageAdded.content);
      } catch (error) {
        console.error('Error parsing message content:', error);
      }

      // Check if the instanceId matches the current platform ID
      if (!parsedData.instanceId || userData?.user?.platform?.id !== parsedData.instanceId) {
        return;
      }

      notification.success({
        message: 'Message',
        description: `${parsedData.message}`,
      });
      setSales([]);
      fetchSales(query); // Fetch latest sales
      fetchStatusCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newSubscriptionData]);

  const getButtonStyle = (value: StatusType, bgColor: string) => ({
    backgroundColor: status === value ? undefined : bgColor, // Keep default Ant Design primary when selected
  });

  const getCountStyle = (value: StatusType) => ({
    fontSize: 12,
    border: '1px solid #999',
    borderRadius: 100,
    padding: '0 4px',
    color: status === value ? '#fff' : '#000',
    transition: 'all 0.3s ease',
  });

  const onSearch = async (values: {
    startDate?: string;
    endDate?: string;
    searchString: string;
  }) => {
    setDateRange((prev) => {
      const startDate = values.startDate || prev.startDate;
      const endDate = values.endDate || prev.endDate;

      return {
        startDate: startDate ? formatToLocalDateTime(new Date(startDate), 0, 0) : undefined, // 12:00 AM
        endDate: endDate ? formatToLocalDateTime(new Date(endDate), 23, 59) : undefined, // 11:59 PM
      };
    });
    setSearchText(values.searchString);
    setPage(1);
    setSales([]); // Clear existing sales when user changes
  };

  const showOrderShowDrawer = (saleId: string) => {
    setSelectedOrderId(saleId);
    setOpenShowDrawer(true);
  };

  const handleUserChange = (value: string) => {
    setSelectedUser(value);
    setPage(1);
    setSales([]); // Clear existing sales when user changes
  };

  const handleStatusChange = (e: RadioChangeEvent) => {
    setStatus(e.target.value);
    setPage(1);
    setSales([]); // Clear existing sales when user changes
  };

  const renderCardView = useCallback(() => {
    if (isLoading && sales.length === 0) {
      return (
        <Space direction="vertical" style={{ width: '100%', gap: 16 }}>
          {[1, 2, 3].map((_, index) => (
            <Card
              key={index}
              style={{
                width: '100%',
                marginBottom: 16,
                border: 'none',
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
              }}
            >
              <Skeleton active />
            </Card>
          ))}
        </Space>
      );
    } else if (!isLoading && sales.length === 0) {
      return <Empty description="No sales found" style={{ marginTop: '3rem' }} />;
    } else {
      return (
        <Space direction="vertical" style={{ width: '100%' }}>
          {sales.map((sale, idx) => (
            <Card
              key={sale.id + '-' + idx}
              style={{
                width: '100%',
                marginBottom: 16,
                border: 'none',
                boxShadow: '0px 2px 20px 0px #2C303C1A',
                borderRadius: '8px',
                // background: getTrackingStatusColor(sale.trackingStatus),
              }}
              actions={[
                <div
                  key="actions"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0 16px',
                    gap: 8,
                  }}
                >
                  <InvoiceButton saleId={sale.id} />

                  <Button
                    type="link"
                    onClick={() => showOrderShowDrawer(sale.id)}
                    icon={<RiEyeLine />}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      justifyContent: 'center',
                      flex: 1, // Make buttons take equal space
                      border: '1px solid #DEEEF8',
                      borderRadius: 4,
                    }}
                  >
                    View
                  </Button>
                </div>,
              ]}
            >
              <Space
                align="center"
                style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}
              >
                <Text>{sale.id}</Text>
                <Tag color={getTagColor(sale.trackingStatus)}>
                  {sale.trackingStatus.toUpperCase()}
                </Tag>
              </Space>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'start',
                }}
              >
                <Text
                  style={{
                    fontFamily: 'Nunito Sans',
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    verticalAlign: 'middle',
                  }}
                >
                  {sale.client.name}
                </Text>
                <Text
                  style={{
                    fontFamily: 'Nunito Sans',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '100%',
                    letterSpacing: '-2%',
                    verticalAlign: 'middle',
                    color: '#09254580',
                    marginTop: '4px',
                  }}
                >
                  {sale.salesProductsCopy.length} Items ordered
                </Text>
                <div
                  style={{
                    display: 'flex',
                    gap: 16,
                    alignItems: 'center',
                    fontFamily: 'Nunito Sans',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '100%',
                    letterSpacing: '-2%',
                    verticalAlign: 'middle',
                    marginTop: '20px',
                  }}
                >
                  {sale.orderType && <Text style={{ color: '#09254580' }}>{sale.orderType}</Text>}
                  {sale.warehouse.name && (
                    <Text style={{ color: '#09254580' }}>{sale.warehouse.name}</Text>
                  )}
                  <Text style={{ color: '#09254580' }}>
                    {sale.salesStatus === EStatus.COMPLETED
                      ? formatTime(
                          Math.floor(
                            (new Date(sale.salesCompletedAt).getTime() -
                              new Date(sale.salesDate).getTime()) /
                              1000
                          )
                        )
                      : formatTime(calculateWaitingTime(sale))}
                  </Text>
                </div>
              </div>
            </Card>
          ))}

          {isLoading && sales.length > 0 && (
            <p style={{ textAlign: 'center', paddingTop: '1rem' }}>
              <Spin />
            </p>
          )}
        </Space>
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sales]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const scrollTop = e.currentTarget.scrollTop;
    const scrollHeight = e.currentTarget.scrollHeight;
    const clientHeight = e.currentTarget.clientHeight;

    const remainingScroll = scrollHeight - (scrollTop + clientHeight);

    if (remainingScroll <= BOTTOM_SCROLL_THRESHOLD && !isLoading) {
      // Don't fetch if all sales were fetched
      if (sales.length >= totalSales) return;
      setPage((prevPage) => prevPage + 1);
    }
  };

  const pageHeader = (
    <Space>
      {canCreateRestaurantPos?.can && (
        <Button
          icon={<Icons.PlusSquareOutlined />}
          type="primary"
          onClick={() => push('/restaurant-pos')}
        >
          Take Order
        </Button>
      )}
    </Space>
  );

  return (
    <div style={{ height: '100vh', overflowY: 'hidden' }}>
      <MetaData />
      <OrderShowDrawer
        open={openShowDrawer}
        orderId={selectedOrderId}
        onClose={() => setOpenShowDrawer(false)}
        width={378}
        viewType="waiter-view"
      />

      <List headerProps={{ extra: pageHeader }}>
        <SearchWithDateRange
          placeholder="Search by Order ID, Client Name"
          size="large"
          className={styles['filter-section__search']}
          index={SearchIndex.Sales}
          groups={['id', 'client']}
          onSearch={onSearch}
          dateRange={getDateRangeOfLastXDays(0)}
        />
        <Select
          style={{ width: '100%', marginBottom: '2rem' }}
          value={selectedUser}
          onChange={handleUserChange}
          options={users.map((u) => ({ value: u.id, label: u.name }))}
          disabled={isLoading}
        />

        <div
          style={{
            marginBottom: '2rem',
            display: 'flex',
            justifyContent: 'space-between',
            overflowX: 'auto', // Enable horizontal scrolling
            whiteSpace: 'nowrap', // Prevent wrapping
          }}
        >
          <Radio.Group
            size="middle"
            buttonStyle="solid"
            value={status}
            onChange={handleStatusChange}
            disabled={isLoading}
          >
            <Radio.Button value="all">All</Radio.Button>
            <Radio.Button value="new" style={getButtonStyle('new', getTrackingStatusColor('new'))}>
              New{' '}
              <span style={{ ...getCountStyle('new') }}>
                {statusCount?.salesStatusCount.orderConfirmed ?? 0}
              </span>
            </Radio.Button>
            <Radio.Button
              value="processing"
              style={getButtonStyle('processing', getTrackingStatusColor('processing'))}
            >
              Processing{' '}
              <span style={{ ...getCountStyle('processing') }}>
                {statusCount?.salesStatusCount.deliveryInProgress ?? 0}
              </span>
            </Radio.Button>
            <Radio.Button
              value="completed"
              style={getButtonStyle('completed', getTrackingStatusColor('completed'))}
            >
              Completed{' '}
              <span style={{ ...getCountStyle('completed') }}>
                {statusCount?.salesStatusCount.completed ?? 0}
              </span>
            </Radio.Button>
          </Radio.Group>
        </div>

        <div style={{ height: '65vh', padding: '1rem', overflowY: 'auto' }} onScroll={handleScroll}>
          {renderCardView()}
        </div>
      </List>
    </div>
  );
};

type InvoiceButtonProps = {
  saleId: string;
};

const InvoiceButton: React.FC<InvoiceButtonProps> = (props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleInvoice = async (id: string) => {
    try {
      setIsLoading(true);
      const invoiceString = await getSaleInvoice(id, EInvoiceType.Sales);
      previewPdfFromBase64(invoiceString);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err?.message || 'Failed to generate invoice',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      type="link"
      onClick={() => handleInvoice(props.saleId)}
      icon={<RiPrinterLine />}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        justifyContent: 'center',
        flex: 1, // Make buttons take equal space
        border: '1px solid #DEEEF8',
        borderRadius: 4,
      }}
      loading={isLoading}
    >
      Invoice
    </Button>
  );
};
